"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
export default function Revenuefaq() {
  const categories = [{
    id: "all",
    label: "ALL"
  }, {
    id: "strategy",
    label: "STRATEGY"
  }, {
    id: "technology",
    label: "TECHNOLOGY"
  }, {
    id: "audit",
    label: "AUDIT"
  }, {
    id: "growth",
    label: "GROWTH"
  }];
  const faqs = [{
    id: "01",
    category: "strategy",
    question: "How do you maximize revenue through OTA strategies?",
    answer: "We optimize your OTA presence by leveraging data-driven pricing, high-converting content, and strategic inventory management to ensure your property captures maximum demand during peak and off-peak seasons.",
    spec: "SN/01 · DATA-DRIVEN · OTA-OPTIMIZED"
  }, {
    id: "02",
    category: "technology",
    question: "Which hospitality tech stacks do you recommend?",
    answer: "We evaluate your current infrastructure and recommend scalable PMS, Channel Managers, and Booking Engines that integrate seamlessly to automate workflows and improve direct booking conversion rates.",
    spec: "SN/02 · INTEGRATED · SCALABLE"
  }, {
    id: "03",
    category: "audit",
    question: "What is included in a professional revenue audit?",
    answer: "Our comprehensive audit analyzes your historical performance, competitor rate positioning, OTA listing health, and digital marketing effectiveness to identify untapped revenue opportunities.",
    spec: "SN/03 · COMPREHENSIVE · ACTIONABLE"
  }, {
    id: "04",
    category: "growth",
    question: "How do you drive direct booking growth?",
    answer: "By optimizing your website for conversion, implementing targeted digital marketing campaigns, and creating exclusive direct-booking incentives, we shift the balance from high-commission OTAs to profitable direct channels.",
    spec: "SN/04 · PROFITABLE · DIRECT-SHIFT"
  }, {
    id: "05",
    category: "strategy",
    question: "Is revenue management suitable for small boutique hotels?",
    answer: "Absolutely. Revenue management is essential for properties of all sizes. We tailor our strategies to your specific market position, ensuring you remain competitive regardless of your property scale.",
    spec: "SN/05 · TAILORED · COMPETITIVE"
  }, {
    id: "06",
    category: "growth",
    question: "What results can I expect from your consulting?",
    answer: "Our clients typically see significant improvements in RevPAR, ADR, and overall occupancy within the first 90 days through disciplined rate management and strategic digital positioning.",
    spec: "SN/06 · REVPAR · ADR-GROWTH"
  }, {
    id: "07",
    category: "technology",
    question: "Do you assist with PMS implementation?",
    answer: "Yes, we provide end-to-end support for tech implementation, from vendor selection and contract negotiation to staff training and system configuration for optimal performance.",
    spec: "SN/07 · END-TO-END · SUPPORTED"
  }, {
    id: "08",
    category: "audit",
    question: "How often should a hotel conduct a revenue audit?",
    answer: "We recommend a professional revenue audit at least twice a year or whenever there is a significant shift in market demand or property management to ensure your strategy remains aligned with goals.",
    spec: "SN/08 · BI-ANNUAL · ALIGNED"
  }];
  const [activeCategory, setActiveCategory] = useState("all");
  const filteredFaqs = activeCategory === "all" ? faqs : faqs.filter(f => f.category === activeCategory);
  return <section data-section-id="4978" id="financialintegrationfaq" className="bg-slate-50 text-slate-900 py-20 md:py-[140px] px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="mb-16 md:mb-24">
          <div className="font-default text-xs uppercase tracking-widest text-blue-600 mb-6">
            § REVENUE MANAGEMENT / FAQ
          </div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] font-semibold leading-[1.05] tracking-tight text-slate-900">
            Expert revenue insights.
          </h2>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="font-default text-[10px] uppercase tracking-widest text-slate-500 mb-6 pb-4 border-b border-slate-200">
                ※ CATEGORY INDEX
              </div>
              <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
                {categories.map((cat, i) => {
                const isActive = activeCategory === cat.id;
                return <Button data-index={i} key={cat.id} onClick={() => setActiveCategory(cat.id)} variant="ghost" className={`font-default text-xs uppercase tracking-widest text-left h-auto py-3 px-4 border-l-2 rounded-md justify-start transition-all whitespace-nowrap ${isActive ? "border-l-blue-600 text-blue-900 bg-white shadow-sm" : "border-l-transparent text-slate-600 hover:text-slate-900 hover:border-l-slate-200 hover:bg-transparent"}`}>
                      <span className="opacity-50 mr-3">
                        {String(i).padStart(2, "0")}
                      </span>
                      {cat.label}
                    </Button>;
              })}
              </nav>

              <div className="mt-10 pt-6 border-t border-slate-200 hidden lg:block">
                <div className="font-default text-[10px] uppercase tracking-widest text-slate-500 space-y-2">
                  <div className="flex justify-between">
                    <span>ENTRIES</span>
                    <span className="text-slate-900">
                      {String(filteredFaqs.length).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>STATUS</span>
                    <span className="text-blue-600">ACTIVE</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {filteredFaqs.map((faq, i) => <AccordionItem data-index={i} key={faq.id} value={faq.id} className="border-b-0 border-t border-slate-200 first:border-t group/item">
                  <AccordionTrigger className="hover:no-underline py-6 [&>svg]:hidden group">
                    <div className="flex items-start gap-6 md:gap-10 w-full pr-4">
                      <span className="font-default text-xs text-slate-400 mt-1 shrink-0">
                        {faq.id}
                      </span>
                      <span className="font-default text-base md:text-lg text-slate-900 text-left flex-1 transition-transform duration-200 group-hover:translate-x-1 group-data-[state=open]:translate-x-1">
                        {faq.question}
                      </span>
                      <span className="shrink-0 mt-1">
                        <Plus className="size-4 text-blue-600 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="ml-0 md:ml-[3.25rem] pl-6 md:pl-10 border-l border-slate-200 space-y-4">
                      <p className="font-default text-[15px] leading-relaxed text-slate-600">
                        {faq.answer}
                      </p>
                      <p className="font-default text-[10px] uppercase tracking-widest text-blue-600/80">
                        {faq.spec}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            {}
            <div className="mt-12 border border-blue-600 bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-lg shadow-md">
              <div>
                <div className="font-default text-[10px] uppercase tracking-widest text-blue-600 mb-2">
                  ※ DIRECT CONSULTATION
                </div>
                <p className="font-default text-sm uppercase tracking-wider text-slate-900">
                  Ready to maximize your revenue?
                </p>
              </div>
              <Button asChild variant="ghost" className="font-default text-xs uppercase tracking-widest text-blue-600 hover:text-blue-700 hover:bg-blue-50 self-start md:self-auto px-4 h-auto py-2 rounded-md">
                <Link to="/ContactPage">
                  BOOK A CONSULTATION
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}